/**
 * Event Load More Handler
 * Implements progressive enhancement pagination for event listings
 * Features: AJAX loading, smooth animations, accessibility support
 */

class EventLoadMore {
    constructor() {
        this.eventList = document.querySelector('.event-list');
        this.loadMoreButton = document.getElementById('load-more-events');
        this.eventCounter = document.querySelector('.event-counter');
        this.currentCountElement = document.querySelector('.current-count');
        this.totalCountElement = document.querySelector('.total-count');
        this.remainingCountElement = document.querySelector('.remaining-count');
        this.ariaLiveRegion = document.querySelector('.sr-only[role="status"]');

        this.currentPage = 1;
        this.isLoading = false;
        this.maxPages = 1;
        this.totalEvents = 0;
        this.currentCount = 0;

        if (!this.eventList || !this.loadMoreButton) {
            return; // Exit if elements don't exist
        }

        this.init();
    }

    init() {
        // Get initial data from DOM
        this.currentPage = parseInt(this.eventList.dataset.currentPage) || 1;
        this.maxPages = parseInt(this.eventList.dataset.maxPages) || 1;
        this.totalEvents = parseInt(this.eventList.dataset.totalEvents) || 0;
        this.currentCount = this.eventList.querySelectorAll('.event-item').length;

        // Bind event listeners
        this.loadMoreButton.addEventListener('click', () => this.loadMore());

        // Optional: Add "Back to Top" functionality after multiple loads
        this.backToTopButton = this.createBackToTopButton();
    }

    async loadMore() {
        if (this.isLoading) return;

        const nextPage = this.currentPage + 1;

        if (nextPage > this.maxPages) {
            this.hideLoadMoreButton();
            return;
        }

        this.setLoadingState(true);

        try {
            const data = await this.fetchEvents(nextPage);

            if (data.success && data.data.html) {
                this.appendEvents(data.data.html);
                this.updateUI(data.data);
                this.currentPage = nextPage;

                // Announce to screen readers
                this.announceUpdate(data.data.current_count, data.data.total);

                // Show back to top button after 2 loads
                if (this.currentPage >= 3) {
                    this.showBackToTop();
                }

                // Hide button if no more events
                if (!data.data.has_more) {
                    this.hideLoadMoreButton();
                }
            }
        } catch (error) {
            console.error('Load more error:', error);
            this.showError();
        } finally {
            this.setLoadingState(false);
        }
    }

    async fetchEvents(page) {
        const params = new URLSearchParams({
            action: 'load_more_events',
            paged: page,
            icr_retail_center: this.eventList.dataset.retailCenter || ''
        });

        // Preserve any active filters from filter form
        const filterForm = document.querySelector('.event-filter-form');
        if (filterForm) {
            const formData = new FormData(filterForm);
            formData.forEach((value, key) => {
                if (value && key !== 'action') {
                    params.append(key, value);
                }
            });
        }

        const ajaxUrl = `${window.location.origin}/wp-admin/admin-ajax.php?${params.toString()}`;
        const response = await fetch(ajaxUrl);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    }

    appendEvents(html) {
        // Create temporary container to parse HTML
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = html;
        const newEvents = tempContainer.querySelectorAll('.event-item');

        // Add fade-in class to new events
        newEvents.forEach((event, index) => {
            event.style.opacity = '0';
            event.classList.add('event-item--loading');
            this.eventList.appendChild(event);

            // Staggered fade-in animation
            setTimeout(() => {
                event.style.transition = 'opacity 0.4s ease';
                event.style.opacity = '1';
                event.classList.remove('event-item--loading');
            }, index * 50);
        });

        // Update current count
        this.currentCount = this.eventList.querySelectorAll('.event-item').length;
    }

    updateUI(data) {
        // Update counter display
        if (this.currentCountElement) {
            this.currentCountElement.textContent = Math.min(data.current_count, data.total);
        }

        if (this.totalCountElement) {
            this.totalCountElement.textContent = data.total;
        }

        // Update remaining count in button
        if (this.remainingCountElement) {
            const remaining = data.total - data.current_count;
            if (remaining > 0) {
                this.remainingCountElement.textContent = `(${remaining} more)`;
            } else {
                this.remainingCountElement.textContent = '';
            }
        }

        // GTM Tracking
        if (window.dataLayer) {
            window.dataLayer.push({
                'event': 'holiday custom event',
                'name': 'load_more_events',
                'page': this.currentPage,
                'total_loaded': data.current_count
            });
        }
    }

    setLoadingState(loading) {
        this.isLoading = loading;
        this.loadMoreButton.dataset.loading = loading;
        this.loadMoreButton.disabled = loading;

        if (loading) {
            this.loadMoreButton.setAttribute('aria-busy', 'true');
            this.loadMoreButton.querySelector('.button-text').textContent = 'Loading...';
        } else {
            this.loadMoreButton.setAttribute('aria-busy', 'false');
            this.loadMoreButton.querySelector('.button-text').textContent = 'Load More Events';
        }
    }

    hideLoadMoreButton() {
        if (this.loadMoreButton) {
            this.loadMoreButton.style.transition = 'opacity 0.3s ease';
            this.loadMoreButton.style.opacity = '0';
            setTimeout(() => {
                this.loadMoreButton.style.display = 'none';
            }, 300);
        }
    }

    announceUpdate(currentCount, total) {
        if (this.ariaLiveRegion) {
            this.ariaLiveRegion.textContent = `Loaded more events. Now showing ${currentCount} of ${total} events.`;
        }
    }

    showError() {
        const errorMessage = document.createElement('p');
        errorMessage.className = 'load-more-error alert';
        errorMessage.textContent = 'Unable to load more events. Please try again.';
        errorMessage.style.textAlign = 'center';
        errorMessage.style.color = 'var(--md-sys-color-error, #B3261E)';

        this.loadMoreButton.parentNode.insertBefore(errorMessage, this.loadMoreButton);

        // Remove error after 5 seconds
        setTimeout(() => errorMessage.remove(), 5000);
    }

    createBackToTopButton() {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'back-to-top-button';
        button.innerHTML = '<span>↑</span> Back to Top';
        button.setAttribute('aria-label', 'Scroll back to top of events');

        button.addEventListener('click', () => {
            const eventWrapper = document.querySelector('.event-wrapper');
            if (eventWrapper) {
                eventWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });

                // Focus on first event for keyboard users
                const firstEvent = this.eventList.querySelector('.event-item a');
                if (firstEvent) {
                    setTimeout(() => firstEvent.focus(), 500);
                }
            }
        });

        // Append to load more wrapper
        const loadMoreWrapper = this.loadMoreButton?.parentNode;
        if (loadMoreWrapper) {
            loadMoreWrapper.appendChild(button);
        }

        return button;
    }

    showBackToTop() {
        if (this.backToTopButton) {
            this.backToTopButton.classList.add('visible');
        }
    }

    // Public method to reset pagination (called by filter)
    reset() {
        this.currentPage = 1;
        this.isLoading = false;
        if (this.backToTopButton) {
            this.backToTopButton.classList.remove('visible');
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    window.eventLoadMore = new EventLoadMore();
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
    module.exports = EventLoadMore;
}
